import React, { useContext, useEffect } from 'react'
import { toast } from 'react-toastify'
import { CircleLoader, FullBlockLoader } from '@deal/components'
import { ChildProps, WithApolloClient, withApollo } from '@apollo/client/react/hoc'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { getFilteredLineItems } from '#src/services/cart'
import { UpdateQuantityForOrderComponent } from '../UpdateQuantityForOrder.generated'
import { RemoveItemFromOrderComponent } from '../RemoveItemFromOrder.generated'
import { SyncShoppingCartMutation } from '../../../containers/Cart/SyncShoppingCart.generated'
import { TrackableSellableFragment } from '../../../fragments/TrackableSellable.generated'
import { LoggedInCartContext } from '#src/containers/Cart'
import EmptyCart from '../components/EmptyCart'
import LineItems from './components/LineItems'
import CartAlerts from './components/CartAlerts'
import Footer from './components/Footer'
import { TouchScrollable } from 'react-scrolllock'
import styles from './styles.css'
import classnames from 'classnames'
import { getSellableEcommerceProperties } from '#src/services/tracking'

interface LoaggedInCartProps {
  grow?: boolean
}

const LoggedInCart: React.FC<ChildProps<
  WithApolloClient<LoaggedInCartProps & WithAnalyticsProps>,
  SyncShoppingCartMutation
>> = ({ analytics, grow = true }) => {
  const loggedInCartContext = useContext(LoggedInCartContext)

  useEffect(() => {
    if (loggedInCartContext && loggedInCartContext.refetch) {
      loggedInCartContext.refetch()
    }
  }, [])

  if (!loggedInCartContext) {
    return null
  }

  const { order, loading } = loggedInCartContext

  if (order) {
    const filteredLineItems = getFilteredLineItems(order.lineItems)

    return (
      <div className={styles.content}>
        <CartAlerts messages={order.messages} />
        <TouchScrollable>
          <div className={classnames(styles.items, { [styles.grow]: grow })}>
            {filteredLineItems.length > 0 ? (
              <RemoveItemFromOrderComponent
                onError={() => {
                  toast.error('Error removing item from cart.')
                }}
              >
                {removeItemsFromOrder => (
                  <UpdateQuantityForOrderComponent>
                    {(updateQuantityForOrderComponent, { loading }) => (
                      <LineItems
                        lineItems={filteredLineItems}
                        onRemoveItem={(lineItemId, sellable?: TrackableSellableFragment) => {
                          removeItemsFromOrder({
                            variables: {
                              input: {
                                lineItemIds: [lineItemId]
                              }
                            }
                          }).then(() => {
                            sellable &&
                              analytics &&
                              analytics.events.trackProductRemoved(
                                getSellableEcommerceProperties(sellable)
                              )
                          })
                        }}
                        onUpdateQuantity={(lineItemId, quantity) => {
                          updateQuantityForOrderComponent({
                            variables: {
                              input: {
                                lineItems: [{ id: lineItemId, quantity }]
                              }
                            }
                          })
                        }}
                        loadingUpdateQuantity={loading}
                      />
                    )}
                  </UpdateQuantityForOrderComponent>
                )}
              </RemoveItemFromOrderComponent>
            ) : (
              <EmptyCart />
            )}
          </div>
        </TouchScrollable>
        {filteredLineItems.length > 0 && (
          <div className={styles.footer}>
            <Footer order={order} />
          </div>
        )}
      </div>
    )
  } else if (loading) {
    return <FullBlockLoader loader={CircleLoader} />
  } else {
    return <EmptyCart />
  }
}

export default withApollo<LoaggedInCartProps>(withAnalytics(LoggedInCart))
